import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  TextField,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
} from '@mui/material';
// @ts-ignore
import { CKEditor } from '@ckeditor/ckeditor5-react';
// @ts-ignore
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { firmService, Firm } from '../services/firmService';
import { useAuth } from '../contexts/AuthContext';
import { firmCustomAgreementAPI } from '../services/api';

const ClassicEditorAny: any = ClassicEditor;

const FirmApproval: React.FC = () => {
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [approvalDialog, setApprovalDialog] = useState<{
    open: boolean;
    firm: Firm | null;
    firmType: 'client' | 'vendor';
    useCustomAgreement: boolean;
    agreementType: 'html' | 'pdf';
    agreementTab: number;
    htmlContent: string;
    pdfFile: File | null;
    customAgreementId: number | null;
    savingAgreement: boolean;
    showRawHtml: boolean;
  }>({
    open: false,
    firm: null,
    firmType: 'client',
    useCustomAgreement: false,
    agreementType: 'html',
    agreementTab: 0,
    htmlContent: '',
    pdfFile: null,
    customAgreementId: null,
    savingAgreement: false,
    showRawHtml: false,
  });
  const [rejectionDialog, setRejectionDialog] = useState<{
    open: boolean;
    firm: Firm | null;
    reason: string;
  }>({
    open: false,
    firm: null,
    reason: '',
  });
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchFirms();
  }, []);

  const fetchFirms = async () => {
    try {
      setLoading(true);
      const response = await firmService.getAllFirms({ status: 'pending' });
      setFirms(response.data.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approvalDialog.firm) return;

    // If custom agreement is enabled but not saved, save it first
    if (approvalDialog.useCustomAgreement && !approvalDialog.customAgreementId) {
      await handleSaveCustomAgreement();
      // Wait a bit for the state to update
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    try {
      setActionLoading(approvalDialog.firm.id);
      
      // Approve the firm with the selected type and custom agreement if applicable
      await firmService.approveFirm(
        approvalDialog.firm.id,
        approvalDialog.firmType,
        approvalDialog.useCustomAgreement ? approvalDialog.customAgreementId || undefined : undefined
      );
      
      setSuccess(`Firm "${approvalDialog.firm.name}" approved successfully as ${approvalDialog.firmType}`);
      setApprovalDialog({
        open: false,
        firm: null,
        firmType: 'client',
        useCustomAgreement: false,
        agreementType: 'html',
        agreementTab: 0,
        htmlContent: '',
        pdfFile: null,
        customAgreementId: null,
        savingAgreement: false,
        showRawHtml: false,
      });
      fetchFirms(); // Refresh the list
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectionDialog.firm) return;

    try {
      setActionLoading(rejectionDialog.firm.id);
      await firmService.rejectFirm(rejectionDialog.firm.id, rejectionDialog.reason);
      setSuccess('Firm rejected successfully');
      setRejectionDialog({ open: false, firm: null, reason: '' });
      fetchFirms(); // Refresh the list
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectionDialog = (firm: Firm) => {
    setRejectionDialog({
      open: true,
      firm,
      reason: '',
    });
  };

  const openApprovalDialog = (firm: Firm) => {
    setApprovalDialog({
      open: true,
      firm,
      firmType: 'client', // Default to client
      useCustomAgreement: false,
      agreementType: 'html',
      agreementTab: 0,
      htmlContent: '',
      pdfFile: null,
      customAgreementId: null,
      savingAgreement: false,
      showRawHtml: false,
    });
  };

  const handleCloneDefault = async () => {
    try {
      const response = await firmCustomAgreementAPI.cloneDefault();
      setApprovalDialog(prev => ({
        ...prev,
        htmlContent: response.data.data.html_content || '',
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to clone default agreement');
    }
  };

  const handleSaveCustomAgreement = async () => {
    if (!approvalDialog.firm) return;

    try {
      setApprovalDialog(prev => ({ ...prev, savingAgreement: true }));
      
      const formData = new FormData();
      formData.append('firm_id', approvalDialog.firm.id.toString());
      formData.append('type', approvalDialog.agreementType);
      formData.append('title', 'Service Agreement');

      if (approvalDialog.agreementType === 'html') {
        formData.append('html_content', approvalDialog.htmlContent);
      } else {
        if (!approvalDialog.pdfFile) {
          setError('Please select a PDF file');
          setApprovalDialog(prev => ({ ...prev, savingAgreement: false }));
          return;
        }
        formData.append('pdf_file', approvalDialog.pdfFile);
      }

      let response: any;
      if (approvalDialog.customAgreementId) {
        response = await firmCustomAgreementAPI.update(approvalDialog.customAgreementId, formData);
      } else {
        response = await firmCustomAgreementAPI.create(formData);
      }

      setApprovalDialog(prev => ({
        ...prev,
        customAgreementId: response.data.data.id,
        savingAgreement: false,
      }));
      setSuccess('Custom agreement saved successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save custom agreement');
      setApprovalDialog(prev => ({ ...prev, savingAgreement: false }));
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending" color="warning" />;
      case 'approved':
        return <Chip label="Approved" color="success" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" />;
      default:
        return <Chip label={status} />;
    }
  };

  const getTypeChip = (type: string) => {
    switch (type) {
      case 'client':
        return <Chip label="Client" color="primary" size="small" />;
      case 'vendor':
        return <Chip label="Vendor" color="secondary" size="small" />;
      default:
        return <Chip label={type} size="small" />;
    }
  };

      const isPmpAdmin = () => user?.roles?.some(role => role.slug === 'pmp_admin') || false;
      if (!isPmpAdmin()) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          You don't have permission to access this page. This page is only available to PMP Admins.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Firm Approval Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and approve pending firm registrations
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : firms.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No pending firm registrations
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Organization Name</TableCell>
                <TableCell>Contact Person</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Current Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Registration Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {firms.map((firm) => (
                <TableRow key={firm.id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {firm.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {firm.registration_data?.full_name || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {firm.registration_data?.email || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {firm.city}, {firm.state}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {firm.country}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getTypeChip(firm.type)}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(firm.status)}
                  </TableCell>
                  <TableCell>
                    {new Date(firm.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => openApprovalDialog(firm)}
                        disabled={actionLoading === firm.id}
                      >
                        {actionLoading === firm.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          'Approve'
                        )}
                      </Button>
                                             <Button
                         variant="outlined"
                         color="error"
                         size="small"
                         onClick={() => openRejectionDialog(firm)}
                         disabled={actionLoading === firm.id}
                       >
                         {actionLoading === firm.id ? (
                           <CircularProgress size={16} />
                         ) : (
                           'Reject'
                         )}
                       </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialog.open}
        onClose={() => setApprovalDialog({
          open: false,
          firm: null,
          firmType: 'client',
          useCustomAgreement: false,
          agreementType: 'html',
          agreementTab: 0,
          htmlContent: '',
          pdfFile: null,
          customAgreementId: null,
          savingAgreement: false,
          showRawHtml: false,
        })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Approve Firm Registration</DialogTitle>
        <DialogContent>
          {approvalDialog.firm && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {approvalDialog.firm.name}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Card variant="outlined" sx={{ flex: '1 1 400px' }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Organization Details
                    </Typography>
                    <Typography variant="body2">
                      <strong>Name:</strong> {approvalDialog.firm.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Contact:</strong> {approvalDialog.firm.contact_phone || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Website:</strong> {approvalDialog.firm.website || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ flex: '1 1 400px' }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Contact Person
                    </Typography>
                    <Typography variant="body2">
                      <strong>Name:</strong> {approvalDialog.firm.registration_data?.full_name || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong> {approvalDialog.firm.registration_data?.email || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Job Title:</strong> {approvalDialog.firm.registration_data?.job_title || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Address Information
              </Typography>
              <Typography variant="body2">
                {approvalDialog.firm.address_1}
                {approvalDialog.firm.address_2 && `, ${approvalDialog.firm.address_2}`}
              </Typography>
              <Typography variant="body2">
                {approvalDialog.firm.city}, {approvalDialog.firm.state} {approvalDialog.firm.zip_code}
              </Typography>
              <Typography variant="body2">
                {approvalDialog.firm.country}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Firm Type</InputLabel>
                <Select
                  value={approvalDialog.firmType}
                  label="Firm Type"
                  onChange={(e) => setApprovalDialog(prev => ({
                    ...prev,
                    firmType: e.target.value as 'client' | 'vendor'
                  }))}
                >
                  <MenuItem value="client">Client</MenuItem>
                  <MenuItem value="vendor">Vendor</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Select the type of firm this organization will be classified as.
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" gutterBottom>
                Custom Service Agreement (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Optionally provide a custom service agreement for this firm. If not provided, the default service agreement will be used.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={approvalDialog.useCustomAgreement}
                    onChange={(e) => setApprovalDialog(prev => ({
                      ...prev,
                      useCustomAgreement: e.target.checked,
                      customAgreementId: e.target.checked ? prev.customAgreementId : null,
                    }))}
                  />
                }
                label="Use custom service agreement for this firm"
              />

              {approvalDialog.useCustomAgreement && (
                <Box sx={{ mt: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                  <Tabs
                    value={approvalDialog.agreementTab}
                    onChange={(_, newValue) => setApprovalDialog(prev => ({
                      ...prev,
                      agreementTab: newValue,
                      agreementType: newValue === 0 ? 'html' : 'pdf',
                    }))}
                    sx={{ mb: 2 }}
                  >
                    <Tab label="HTML Editor" />
                    <Tab label="PDF Upload" />
                  </Tabs>

                  {approvalDialog.agreementTab === 0 ? (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleCloneDefault}
                        >
                          Clone Default Agreement
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSaveCustomAgreement}
                          disabled={approvalDialog.savingAgreement || !approvalDialog.htmlContent.trim()}
                        >
                          {approvalDialog.savingAgreement ? (
                            <CircularProgress size={16} />
                          ) : (
                            approvalDialog.customAgreementId ? 'Update Agreement' : 'Save Agreement'
                          )}
                        </Button>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={approvalDialog.showRawHtml}
                              onChange={(e) => setApprovalDialog(prev => ({ ...prev, showRawHtml: e.target.checked }))}
                            />
                          }
                          label="Edit raw HTML (preserves all styles and tags)"
                        />
                      </Box>
                      {approvalDialog.showRawHtml ? (
                        <TextField
                          fullWidth
                          multiline
                          minRows={20}
                          value={approvalDialog.htmlContent}
                          onChange={(e) => setApprovalDialog(prev => ({ ...prev, htmlContent: e.target.value }))}
                          sx={{
                            '& .MuiInputBase-root': {
                              fontFamily: 'monospace',
                              fontSize: '12px',
                            }
                          }}
                          placeholder="Paste your HTML content here..."
                        />
                      ) : (
                        <Box sx={{ border: '1px solid #ccc', borderRadius: 1 }}>
                          <CKEditor
                            editor={ClassicEditorAny}
                            data={approvalDialog.htmlContent}
                            onChange={(_event: unknown, editor: any) => {
                              const data = editor.getData();
                              setApprovalDialog(prev => ({ ...prev, htmlContent: data }));
                            }}
                          />
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Supports tokens: [CURRENT_DATE], [ORGANIZATION_FIRM], [NAME], [EMAIL_ADDRESS], [DESIGNATION], etc.
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSaveCustomAgreement}
                          disabled={approvalDialog.savingAgreement || !approvalDialog.pdfFile}
                        >
                          {approvalDialog.savingAgreement ? (
                            <CircularProgress size={16} />
                          ) : (
                            approvalDialog.customAgreementId ? 'Update Agreement' : 'Save Agreement'
                          )}
                        </Button>
                      </Box>
                      <input
                        accept="application/pdf"
                        style={{ display: 'none' }}
                        id="pdf-upload"
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setApprovalDialog(prev => ({ ...prev, pdfFile: file }));
                          }
                        }}
                      />
                      <label htmlFor="pdf-upload">
                        <Button variant="outlined" component="span" fullWidth>
                          {approvalDialog.pdfFile ? approvalDialog.pdfFile.name : 'Select PDF File'}
                        </Button>
                      </label>
                      {approvalDialog.pdfFile && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Selected: {approvalDialog.pdfFile.name} ({(approvalDialog.pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      )}
                    </Box>
                  )}

                  {approvalDialog.customAgreementId && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Custom agreement saved successfully. It will be attached to the firm upon approval.
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApprovalDialog({
              open: false,
              firm: null,
              firmType: 'client',
              useCustomAgreement: false,
              agreementType: 'html',
              agreementTab: 0,
              htmlContent: '',
              pdfFile: null,
              customAgreementId: null,
              savingAgreement: false,
              showRawHtml: false,
            })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={actionLoading === approvalDialog.firm?.id || (approvalDialog.useCustomAgreement && !approvalDialog.customAgreementId && approvalDialog.savingAgreement)}
          >
            {actionLoading === approvalDialog.firm?.id ? (
              <CircularProgress size={16} />
            ) : (
              'Approve Firm'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectionDialog.open}
        onClose={() => setRejectionDialog({ open: false, firm: null, reason: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reject Firm Registration</DialogTitle>
        <DialogContent>
          {rejectionDialog.firm && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {rejectionDialog.firm.name}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                Please provide a reason for rejecting this firm registration:
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Rejection Reason"
                value={rejectionDialog.reason}
                onChange={(e) => setRejectionDialog(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
                placeholder="Enter the reason for rejection..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectionDialog({ open: false, firm: null, reason: '' })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={actionLoading === rejectionDialog.firm?.id || !rejectionDialog.reason.trim()}
          >
            {actionLoading === rejectionDialog.firm?.id ? (
              <CircularProgress size={16} />
            ) : (
              'Reject Firm'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FirmApproval;
